/**
 * TrialExpiredRedirect Component
 * 
 * Automatically redirects users to upgrade page when their trial expires.
 * Checks trialEndsAt from subscription status and redirects if trial has ended.
 * Also shows feedback dialog if user hasn't upgraded after trial expiration.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useUser } from '@clerk/clerk-react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TrialFeedbackDialog from './TrialFeedbackDialog';

const TrialExpiredRedirect: React.FC = () => {
  const { trialEndsAt, subscriptionTier, loading, isPremium } = useSubscription();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [hasShownFeedback, setHasShownFeedback] = useState(false);

  useEffect(() => {
    // Don't redirect if still loading or not a Pro user
    if (loading || subscriptionTier !== 'pro') {
      return;
    }

    // Don't redirect if already on upgrade page or payment success page
    const publicRoutes = [
      '/payment-upgrade',
      '/payment-success',
      '/landing',
      '/sign-in',
      '/sign-up'
    ];
    
    const isPublicRoute = publicRoutes.some(route => 
      location.pathname.startsWith(route) || location.pathname === route
    );

    if (isPublicRoute) {
      return;
    }

    // Check if trial has expired
    if (trialEndsAt) {
      const trialEndDate = trialEndsAt instanceof Date ? trialEndsAt : new Date(trialEndsAt);
      const now = new Date();
      
      // If trial has ended (more than 1 minute past trial end to account for clock skew)
      if (trialEndDate < now) {
        // Check if user is still premium (they upgraded) or not (trial expired without upgrade)
        if (!isPremium && !hasShownFeedback && user?.id) {
          // Check if we've already shown feedback for this user
          const checkFeedbackShown = async () => {
            try {
              const userRef = doc(db, 'users', user.id);
              const userDoc = await getDoc(userRef);
              const userData = userDoc.data();
              
              // Only show feedback if they haven't provided it yet
              if (!userData?.trialFeedbackShown) {
                setShowFeedbackDialog(true);
                // Mark as shown in user document
                await updateDoc(userRef, { trialFeedbackShown: true });
              }
            } catch (error) {
              console.error('Error checking feedback status:', error);
            }
          };
          
          checkFeedbackShown();
        }
        
        console.log('Trial expired, redirecting to upgrade page');
        navigate('/payment-upgrade?trial_expired=true', { replace: true });
      }
    }
  }, [trialEndsAt, subscriptionTier, loading, isPremium, navigate, location.pathname, user?.id, hasShownFeedback]);

  return (
    <>
      {showFeedbackDialog && user?.id && (
        <TrialFeedbackDialog
          open={showFeedbackDialog}
          onClose={() => {
            setShowFeedbackDialog(false);
            setHasShownFeedback(true);
          }}
          userId={user.id}
          userEmail={user.primaryEmailAddress?.emailAddress}
        />
      )}
    </>
  );
};

export default TrialExpiredRedirect;
